import { apiService } from './api';
import { SAGE_COMPANY_ID } from '../constants/sage';
import type { AgentSalesRepLink, SageSalesRep, SalesRepCandidate } from '../types/sage';

/**
 * Sage Sales Representatives and the Agent ↔ Sales Rep link (DRI-85). Every endpoint is FinanceWrite.
 * The rep list is cached server-side for 30 minutes; `refreshSalesReps` clears that cache.
 */
export const sageSalesRepService = {
  getSalesReps: async (): Promise<SageSalesRep[]> => {
    const response = await apiService.get<SageSalesRep[]>('/sage/sales-reps', { companyId: SAGE_COMPANY_ID });
    return response.data ?? [];
  },

  /** Clears the server's 30-minute rep cache, then re-reads the list. */
  refreshSalesReps: async (): Promise<SageSalesRep[]> => {
    await apiService.post(`/sage/sales-reps/invalidate-cache?companyId=${SAGE_COMPANY_ID}`);
    return sageSalesRepService.getSalesReps();
  },

  getAgentLink: async (agentId: number): Promise<AgentSalesRepLink> => {
    const response = await apiService.get<AgentSalesRepLink>(`/sage-entity-links/agents/${agentId}/sales-rep`);
    return response.data ?? { agentId };
  },

  linkAgent: async (agentId: number, sageSalesRepId: number): Promise<AgentSalesRepLink> => {
    const response = await apiService.put<AgentSalesRepLink>(
      `/sage-entity-links/agents/${agentId}/sales-rep`, { sageSalesRepId });
    return response.data ?? { agentId, sageSalesRepId };
  },

  unlinkAgent: async (agentId: number): Promise<void> => {
    await apiService.delete(`/sage-entity-links/agents/${agentId}/sales-rep`);
  },

  /**
   * Creates a rep in Sage from the agent and links it. A 409 `SALES_REP_EXISTS` propagates as a
   * normalized error; read its candidate with {@link salesRepCandidateFromError}.
   */
  createForAgent: async (agentId: number): Promise<AgentSalesRepLink> => {
    const response = await apiService.post<AgentSalesRepLink>(`/sage-entity-links/agents/${agentId}/sales-rep/create`);
    return response.data ?? { agentId };
  },
};

/** The matching rep carried by a 409 `SALES_REP_EXISTS` error, or null for any other error. */
export function salesRepCandidateFromError(err: unknown): SalesRepCandidate | null {
  if (typeof err !== 'object' || err === null) return null;
  const e = err as { errorCode?: string; errorContext?: Record<string, unknown> | null };
  if (e.errorCode !== 'SALES_REP_EXISTS' || !e.errorContext) return null;
  const ctx = e.errorContext;
  const id = Number(ctx.candidateId);
  if (!Number.isFinite(id) || id <= 0) return null;
  return {
    id,
    name: String(ctx.candidateName ?? ''),
    email: String(ctx.candidateEmail ?? ''),
    active: ctx.candidateActive !== false,
    matchedOn: String(ctx.matchedOn ?? ''),
  };
}

/**
 * Preview of the name Sage will get: the trimmed agent name split on its LAST space — first name is
 * everything before it, last name the last word (empty for a one-word name), each capped at 50.
 * Mirrors the backend's AgentSalesRepLinkService.BuildCreateRequest.
 */
export function previewSalesRepName(agentName: string): { firstName: string; lastName: string } {
  const name = agentName.trim();
  const lastSpace = name.lastIndexOf(' ');
  const firstName = lastSpace < 0 ? name : name.slice(0, lastSpace).trimEnd();
  const lastName = lastSpace < 0 ? '' : name.slice(lastSpace + 1);
  return { firstName: firstName.slice(0, 50), lastName: lastName.slice(0, 50) };
}

/** Display name for a rep: Sage's Name, else "First Last". */
export function salesRepDisplayName(rep: Pick<SageSalesRep, 'name' | 'firstName' | 'lastName'>): string {
  return rep.name?.trim() || `${rep.firstName ?? ''} ${rep.lastName ?? ''}`.trim();
}
